import { logger } from '@modules/logger';
import { FC, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type PopupType = 'card' | 'team-card' | 'email-signature' | 'team-member';

interface IHighlightPopupServiceContext {
  currentPopup?: PopupType | null;
  nextPopups: PopupType[];
  updateCurrentPopup: (popup: PopupType) => void;
  updateNextPopups: (popups: PopupType[]) => void;
  currentPopupRect: DOMRect | null;
  closePopup: () => void;
  updateCurrentPopupRect: (popupRect: DOMRect | null) => void;
}

/** These keys are mainly for storing the flags in the local storage */
const CACHE_POPUP_KEY = 'OPEN_POPUP';
const CACHE_POPUPS_KEY = 'OPEN_NEXT_POPUPS';

const HighlightPopupServiceContext = createContext<IHighlightPopupServiceContext>({
  currentPopup: null,
  nextPopups: [],
  updateCurrentPopup: () => logger.warn('Warning! updateCurrentPopup is not implemented'),
  updateNextPopups: () => logger.warn('Warning! updateNextPopups is not implemented'),
  currentPopupRect: null,
  closePopup: () => logger.warn('Warning! closePopup is not implemented'),
  updateCurrentPopupRect: () => logger.warn('Warning! updateCurrentPopupRect is not implemented'),
});

const HighlightPopupService: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [currentPopup, setCurrentPopup] = useState<PopupType | null>(null);
  const [nextPopups, setNextPopups] = useState<PopupType[]>([]);
  const [currentPopupRect, setCurrentPopupRect] = useState<DOMRect | null>(null);
  const currentRoute = useRef<string>();

  const updateCurrentPopup = useCallback((popup: PopupType) => {
    setCurrentPopup(popup);
    window.localStorage.setItem(CACHE_POPUP_KEY, popup);
  }, []);

  const updateCurrentPopupRect = useCallback((popupRect: DOMRect | null) => {
    setCurrentPopupRect(popupRect);
  }, []);

  const updateNextPopups = useCallback((popups: PopupType[]) => {
    window.localStorage.setItem(CACHE_POPUPS_KEY, JSON.stringify(popups));
    setNextPopups(popups);
  }, []);

  const closePopup = useCallback(() => {
    window.localStorage.removeItem(CACHE_POPUP_KEY);
    setCurrentPopup(null);
  }, []);

  useEffect(() => {
    currentRoute.current = window.location.pathname;

    const cachedPopup = window.localStorage.getItem(CACHE_POPUP_KEY);
    if (cachedPopup) {
      setCurrentPopup(cachedPopup as PopupType);

      return;
    }

    const stringifiedCachedNextPopups = window.localStorage.getItem(CACHE_POPUPS_KEY);
    const cachedNextPopups = stringifiedCachedNextPopups ? JSON.parse(stringifiedCachedNextPopups) : [];
    if (cachedPopup) {
      setNextPopups(cachedNextPopups);

      return;
    }
  }, []);

  return (
    <HighlightPopupServiceContext.Provider
      value={{
        currentPopup,
        nextPopups,
        updateCurrentPopup,
        updateNextPopups,
        currentPopupRect,
        closePopup,
        updateCurrentPopupRect,
      }}
    >
      {children}
    </HighlightPopupServiceContext.Provider>
  );
};

function useHighlightPopupService(): IHighlightPopupServiceContext {
  const services = useContext(HighlightPopupServiceContext);

  return useMemo(() => services, [services]);
}

export { HighlightPopupService, useHighlightPopupService };
